/* eslint-disable @typescript-eslint/no-explicit-any */
import NextApp from 'next/app';
import React, { useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import rtlPlugin from 'stylis-plugin-rtl';
import { StyleSheetManager } from 'styled-components';
import path from 'path';
import { ModelManager } from '@adobe/aem-spa-page-model-manager';
import { useReportWebVitals } from 'next/web-vitals';

import '@glidejs/glide/src/assets/sass/glide.core.scss';
import '@glidejs/glide/src/assets/sass/glide.theme.scss';
import '../styles/homepage.scss'; // TODO: once new Global nav implemention complete we have to remove this

import { defineComponentMapping, getAllowedComponentsForNonAuthorMode } from '../import-components';
import {
  CustomModelClient,
  canUseDOM,
  respGridUtil,
  populatePIdataLayer,
  UserSessionProvider,
  DynamicDCAPageProvider,
  getEnvProp,
} from '@marriott/mi-headless-utils';
import {
  useClientEnvVarsStore,
  usePersistentGlobalStore,
  useDCAPageModelStore,
  useScrollStore,
} from '@marriott/mi-store-utils';
import { setSubDirectoryPrefix } from '@marriott/mi-shop-components/utils';
import { LocaleProvider } from '@marriott/mi-shop-components';
import { useRouter } from 'next/router';
import globalStylesPkg from '@marriott/global-styles/package.json';
import { envVarKeys } from '../_constants/envVarKeys';
import { sessionTimeoutManager } from '@marriott/shared/mi-helper-utils';
import { FEATURE_FLAGS, fetchFlag, TARGETS } from '@marriott/mi-feature-flag-utils';

const version = globalStylesPkg.version;
path.resolve('./next.config.js');

declare global {
  interface Window {
    jQuery: unknown;
    $: any;
    deployedEnvType: string;
  }
}

const paramsString = canUseDOM ? window.location.search : '';
const searchParams = new URLSearchParams(paramsString);
const modelClient = new CustomModelClient(process.env.NEXT_PUBLIC_AEM_HOST);
const useIsomorphicLayoutEffect = canUseDOM ? useLayoutEffect : useEffect;

if (process.env.NODE_ENV !== 'test') {
  if (searchParams?.get('wcmmode') !== 'disabled') {
    ModelManager.initializeAsync({
      modelClient,
    });
  }
}

const App = function (props: any) {
  const { Component, pageProps } = props;
  const rtlPluginTs = (...args: unknown[]) => {
    return rtlPlugin(args[0] as number, args[1] as string);
  };
  const {
    model,
    pagePath,
    isAuthorMode,
    requestId,
    currentLocale,
    apolloEnvVars,
    NEXT_PUBLIC_FETCH_OFFERS_API_URL,
    TARGET_PROPERTY_TOKEN,
    NEXT_GLOBAL_ASSET_PREFIX,
    featureFlagLogo,
    featureFlagSessionTimeout,
    sessionTimeoutMs,
    featureFlagLegacyNav,
    featureFlagEnableConcurFlow,
    featureFlagGeoTargeting,
  } = pageProps;

  let { featureFlags } = pageProps;

  // Get sessionData from global store
  const isClient = typeof window !== 'undefined';

  // Check if user is logged in - only on client side
  const isLoggedIn = React.useMemo(() => {
    if (!isClient) return false;

    const sessionData = (window as any).sessionData;
    return (
      sessionData?.data?.AriesCommon?.memState === 'authenticated' ||
      sessionData?.cacheData?.data?.AriesCommon?.memState === 'authenticated'
    );
  }, [isClient]);

  useEffect(() => {
    if (
      !isAuthorMode &&
      typeof window !== 'undefined' &&
      featureFlagSessionTimeout['experience_mcom_session_timeout_migration'] &&
      sessionTimeoutMs > 0
    ) {
      console.log('Session Timeout - initializing with timeout:', sessionTimeoutMs);
      sessionTimeoutManager.initialize({
        timeoutMs: sessionTimeoutMs,
        isLoggedIn,
        onTimeout() {
          if (typeof window === 'undefined') return;

          console.log('Session related App specific functionality can be added here');
        },
        onUserActivity() {
          console.log('User activity detected - session timer reset');
        },
      });
    }
  }, [sessionTimeoutMs, isLoggedIn, isAuthorMode, featureFlagSessionTimeout]);

  // Feature flag: when true, defers synchronous initialization work to useEffect
  // to reduce pre-hydration blocking. When false, runs original synchronous code.
  // fetchFlag returns { flag_name: value }, so extract the boolean from the object.
  // Local dev always uses the defer path: it skips the synchronous 270 kB JSON deep-clone of the
  // model on hydration (a major local hydration cost). Production stays controlled by the flag.
  const deferHydration =
    process.env.NODE_ENV === 'development' ||
    pageProps?.featureFlagDeferHydration?.experience_mcom_homepage_defer_hydration === true;

  const { query } = useRouter();

  // This brandCode is just for local development and testing. It supplies the brandCode to load brandCSS below.
  // Do not use for production features!
  const devBrandCode = query.brandCode;

  // next performance analytics for more info: https://nextjs.org/docs/pages/api-reference/functions/use-report-web-vitals
  useReportWebVitals(metric => {
    // do not render performance analytics to the console in prod
    if (/dev|test|uat|perf/i.test(pageProps?.DEPLOYED_ENV_CONFIG)) {
      console.log('next/web-vitals - Metric: %s', metric.name, {
        metric,
      });
    }
  });

  const isWebSDKEnabled = getEnvProp(envVarKeys.NEXT_PUBLIC_ENABLE_WEBSDK) === 'true' && model?.enableWebSDK === 'true';
  const experimentName = typeof model?.experimentName === 'string' ? model.experimentName : '';
  const variantName = typeof model?.variantName === 'string' ? model.variantName : '';
  const responsivegrid = model?.cqItems?.root?.[':items']?.responsivegrid;
  const prodDNS = 'https://www.marriott.com';

  const initialMboxExtractionContext = useMemo(() => {
    const gridItems = model?.cqItems?.root?.[':items']?.responsivegrid?.[':items'] || {};
    const dynamictabbedlayoutComp = gridItems?.['dynamictabbedlayout'];
    const tabDetails: any[] = dynamictabbedlayoutComp?.tabDetails || [];
    if (!dynamictabbedlayoutComp || !Array.isArray(tabDetails) || tabDetails.length === 0) return undefined;

    const requestedTab: string | undefined = dynamictabbedlayoutComp?.requestedTab || undefined;
    const requestedLower = String(requestedTab || '').toLowerCase();
    const chosenTab =
      requestedTab && requestedLower
        ? tabDetails.find(t => String(t?.tabName || '').toLowerCase() === requestedLower)
        : undefined;
    const tabToUse = chosenTab ?? tabDetails[0];
    const dcaTiles = tabToUse?.DCAtiles || tabToUse?.dcaTilesList || [];
    const componentType = String(dynamictabbedlayoutComp?.cqType ?? dynamictabbedlayoutComp?.[':type'] ?? '');
    const tiles: any[] = Array.isArray(dcaTiles) ? dcaTiles : [];
    const mboxList = Array.from(
      new Set(
        tiles
          .map((t: any) => t?.mboxParameter)
          .filter((s: unknown): s is string => typeof s === 'string' && s.length > 0)
      )
    );
    if (!componentType || mboxList.length === 0) return undefined;

    const mboxCellCodes: Record<string, string> = {};
    tiles.forEach((t: any) => {
      const mbox = t?.mboxParameter;
      if (typeof mbox === 'string' && mbox.length > 0) {
        mboxCellCodes[mbox] = typeof t?.cellCodes === 'string' ? t.cellCodes : '';
      }
    });

    return {
      componentType,
      mboxList,
      mboxCellCodes,
    };
  }, [model]);

  // Get store actions
  const setPageModel = useDCAPageModelStore(state => state.setPageModel);
  const setEnvVars = useClientEnvVarsStore(state => state.setEnvVars);
  const mergeEnvVars = useClientEnvVarsStore((state: any) => state.mergeEnvVars);
  const initialize = useScrollStore(state => state.initialize);
  const updateGlobalData = usePersistentGlobalStore((state: any) => state.updateGlobalData);

  // Track if component mapping has been initialized (used when flag is ON)
  const componentMappingInitialized = useRef(false);

  // =========================================================================
  // Hooks must be called unconditionally (Rules of Hooks).
  // Memoize values consumed by the hydration-safe setup effect below.
  // =========================================================================

  const respGridModel = useMemo(() => respGridUtil(responsivegrid), [responsivegrid]);

  const deferredEnvVars = useMemo(() => {
    const vars = apolloEnvVars ? { ...apolloEnvVars } : {};
    vars['NEXT_PUBLIC_FETCH_OFFERS_API_URL'] = NEXT_PUBLIC_FETCH_OFFERS_API_URL;
    vars['TARGET_PROPERTY_TOKEN'] = TARGET_PROPERTY_TOKEN;
    vars['SESSION_APP_POST_CALL_URL'] = pageProps?.SESSION_APP_POST_CALL_URL;
    vars['NEXT_PUBLIC_PREFIX'] = pageProps?.NEXT_PUBLIC_PREFIX;
    vars['NEXT_PUBLIC_ENABLE_DCA_V2'] = getEnvProp(envVarKeys.NEXT_PUBLIC_ENABLE_DCA_V2);
    vars['NEXT_PUBLIC_ENABLE_WEBSDK'] = isWebSDKEnabled;
    vars['GET_DESTINATION_TYPE_FLAG'] = pageProps?.GET_DESTINATION_TYPE_FLAG;
    vars['featureFlags'] = {
      ...(featureFlags && typeof featureFlags === 'object' ? featureFlags : {}),
      ...(featureFlagEnableConcurFlow ?? {}),
      ...(featureFlagGeoTargeting ?? {}),
    };
    return vars;
  }, [
    apolloEnvVars,
    NEXT_PUBLIC_FETCH_OFFERS_API_URL,
    TARGET_PROPERTY_TOKEN,
    pageProps?.SESSION_APP_POST_CALL_URL,
    pageProps?.NEXT_PUBLIC_PREFIX,
    pageProps?.GET_DESTINATION_TYPE_FLAG,
    isWebSDKEnabled,
    featureFlags,
    featureFlagEnableConcurFlow,
    featureFlagGeoTargeting,
  ]);

  const deferredMergedEnvVars = useMemo(
    () => ({
      SESSION_GET_CALL_URL_CLIENT: pageProps?.SESSION_GET_CALL_URL_CLIENT,
      NEXT_PUBLIC_SUBMIT_SEARCH_URL: pageProps?.NEXT_PUBLIC_SUBMIT_SEARCH_URL,
      NEXT_PUBLIC_AVAILABILITY_SEARCH_URL: pageProps?.NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
      GOOGLE_MAP_API_KEY: pageProps?.GOOGLE_MAP_API_KEY,
      UXL_IMAGE_CACHE_DOMAIN: pageProps?.UXL_IMAGE_CACHE_DOMAIN,
      SESSION_APP_GET_CALL_V1_URL: pageProps?.SESSION_APP_GET_CALL_V1_URL,
      BAIDU_MAP_API_KEY: pageProps?.BAIDU_MAP_API_KEY,
      REQUEST_ID: requestId,
      ACCEPT_LANGUAGE: currentLocale,
      SET_FALLBACK_TO_CONVOAI: pageProps?.SET_FALLBACK_TO_CONVOAI,
    }),
    [
      pageProps?.SESSION_GET_CALL_URL_CLIENT,
      pageProps?.NEXT_PUBLIC_SUBMIT_SEARCH_URL,
      pageProps?.NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
      pageProps?.GOOGLE_MAP_API_KEY,
      pageProps?.UXL_IMAGE_CACHE_DOMAIN,
      pageProps?.SESSION_APP_GET_CALL_V1_URL,
      pageProps?.BAIDU_MAP_API_KEY,
      requestId,
      currentLocale,
      pageProps?.SET_FALLBACK_TO_CONVOAI,
    ]
  );

  const memoizedFeatureFlags = useMemo(
    () => ({ ...featureFlags, featureFlagLogo, featureFlagLegacyNav, featureFlagEnableConcurFlow }),
    [featureFlags, featureFlagLogo, featureFlagLegacyNav, featureFlagEnableConcurFlow]
  );

  useIsomorphicLayoutEffect(() => {
    const pageModelForStore = deferHydration ? respGridModel : JSON.parse(JSON.stringify(respGridModel));
    setPageModel(pageModelForStore);
    setEnvVars(deferredEnvVars);
    mergeEnvVars(deferredMergedEnvVars);
    if (model) {
      setSubDirectoryPrefix(model.subDirectoryContextPath ? model.subDirectoryContextPath : '');
    }

    if (canUseDOM) {
      populatePIdataLayer();
    }

    if (!!responsivegrid && !isAuthorMode) {
      initialize(responsivegrid);
    }
  }, [
    deferHydration,
    respGridModel,
    deferredEnvVars,
    deferredMergedEnvVars,
    model,
    responsivegrid,
    isAuthorMode,
    setPageModel,
    setEnvVars,
    mergeEnvVars,
    initialize,
  ]);

  const modelAllowedComponents = responsivegrid?.allowedComponents?.components;

  // Wrap with useMemo to avoid recomputing on every render
  const allowedComponents = useMemo(() => {
    if (!model) return undefined;
    if ((modelAllowedComponents?.length ?? 0) > 0 || isAuthorMode) return modelAllowedComponents;
    return getAllowedComponentsForNonAuthorMode(model);
  }, [model, modelAllowedComponents, isAuthorMode]);

  if (deferHydration) {
    featureFlags = memoizedFeatureFlags;
    if (!componentMappingInitialized.current) {
      defineComponentMapping(allowedComponents, pagePath, isAuthorMode, pageProps, featureFlags);
      componentMappingInitialized.current = true;
    }
  } else {
    // Keep component mapping synchronous so AEM mapped components render on the first pass.
    featureFlags = { ...featureFlags, featureFlagLogo, featureFlagLegacyNav };
    defineComponentMapping(allowedComponents, pagePath, isAuthorMode, pageProps, featureFlags);
  }

  // Author mode stylesheet injection (unchanged, always in useEffect)
  useEffect(() => {
    if (!isAuthorMode) {
      return;
    }
    let remoteURL: URL | string;
    try {
      remoteURL = new URL(document.body.dataset.remoteUrl || '');
      remoteURL = remoteURL.origin;
    } catch (e) {
      remoteURL = '';
    }
    const assetUrl = `${process.env.NEXT_PUBLIC_ASSET_DOMAIN || remoteURL || prodDNS}${NEXT_GLOBAL_ASSET_PREFIX}`;
    const globalStyle = document.createElement('link');

    globalStyle.rel = 'stylesheet';
    globalStyle.href =
      assetUrl +
      (model?.rtlEnable == 'true'
        ? `global-styles/${version}/marriot.global.rtl.min.css`
        : `global-styles/${version}/marriot.global.min.css`);
    document.head.appendChild(globalStyle);
  }, [isAuthorMode, NEXT_GLOBAL_ASSET_PREFIX, model?.rtlEnable]);

  // Session data update (unchanged, always in useEffect)
  useEffect(() => {
    updateGlobalData('sessionData', pageProps?.sessionData);
  }, [pageProps?.sessionData, updateGlobalData, isWebSDKEnabled]);

  useEffect(() => {
    if (!canUseDOM) return;
    document.body?.setAttribute('data-experiment-name', experimentName);
    document.body?.setAttribute('data-variant', variantName);
  }, [experimentName, variantName]);

  Object.defineProperty(rtlPluginTs, 'name', { value: 'rtlPluginMonorepo' });

  // Use currentLocale from server-side props, default to en-US
  const locale = currentLocale || 'en-US';

  return (
    <LocaleProvider locale={locale}>
      <div data-experiment-name={experimentName} data-variant={variantName}>
        {devBrandCode && pageProps?.INJECT_BRAND_CSS === 'true' && (
          <link
            rel="stylesheet"
            href={`https://www.marriott.com/etc.clientlibs/mcom-book-spa/brandingcontent.${devBrandCode}.css`}
            as="style"
          />
        )}
        {/*
          initialMboxExtractionContext (optional): use this when you want to conditionally limit which mboxes are extracted/called.
          - componentType: the AEM component type to match (compared against `:type` / `cqType` in the page model)
          - mboxList: allow-list of mbox names to extract/call for the matched componentType
          Example: only include the active tab / selected slot mboxes, instead of extracting mboxes for all tabs/slots on initial load.
        */}
        {process.env.NODE_ENV !== 'test' ? (
          <StyleSheetManager stylisPlugins={pageProps?.model?.rtlEnable === 'true' ? [rtlPluginTs] : []}>
            <UserSessionProvider pageProps={pageProps} includePiData>
              <DynamicDCAPageProvider
                Component={Component}
                pageProps={pageProps}
                initialMboxExtractionContext={initialMboxExtractionContext}
              />
            </UserSessionProvider>
          </StyleSheetManager>
        ) : (
          <UserSessionProvider pageProps={pageProps} includePiData>
            <DynamicDCAPageProvider
              Component={Component}
              pageProps={pageProps}
              initialMboxExtractionContext={initialMboxExtractionContext}
            />
          </UserSessionProvider>
        )}
      </div>
    </LocaleProvider>
  );
};

// Fetch feature flag at the app level so it applies to all routes
App.getInitialProps = async (appContext: any) => {
  // Call page's getInitialProps if it exists
  const appProps = await NextApp.getInitialProps(appContext);

  // Fetch session timeout feature flag server-side
  const featureFlagSessionTimeout = await fetchFlag(FEATURE_FLAGS.EXPERIENCE_MCOM_SESSION_TIMEOUT_MIGRATION, {
    target: TARGETS.APP.HOMEPAGE_RENDERER,
    enableStream: true,
  });

  // Get session timeout from runtime env var (only available server-side)
  const sessionTimeoutMs = process.env.NEXT_PUBLIC_SESSION_TIMEOUT_MS
    ? parseInt(process.env.NEXT_PUBLIC_SESSION_TIMEOUT_MS, 10) * 1000
    : 0;

  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      featureFlagSessionTimeout,
      sessionTimeoutMs,
    },
  };
};

export default App;
